import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProgramGoalTimeline from '../../components/programs/ProgramGoalTimeline';
import {
  CREATE_ACCOUNT_LOP30_URL,
  LOP30_PROGRAM_ID,
  getLop30ProgramDeepLink
} from '../../utils/programDeepLinks';
import { tryOpenAppThenStore } from '../../utils/appStoreLinks';
import { appendUtmToUrl, getStoredUtmParams } from '../../utils/utmTracking';
import './Lop30MinutterLanding.css';

const highlightCards = [
  {
    title: 'Progressiv oppbygging',
    text: 'Vi starter rolig og bygger gradvis utholdenhet og løpeglede.',
    icon: 'fas fa-chart-line q'
  },
  {
    title: 'Kortere steg – lettere løping',
    text: 'Vi lærer deg å løpe med lettere steg og bedre rytme – som gir mindre belastning og mer løpeglede.',
    icon: 'fas fa-shoe-prints'
  },
  {
    title: 'Musikk + guiding',
    text: 'Du blir guidet gjennom hver økt med musikk og coaching på øret. Det gir motivasjon, flyt og struktur.',
    icon: 'fas fa-headphones-alt'
  },
  {
    title: 'Mestring før prestasjon',
    text: 'Målet er ikke å presse deg hardest mulig. Målet er å bygge trygghet, kontinuitet og flyt – steg for steg.',
    icon: 'far fa-heart'
  }
];

const planCards = [
  {
    week: 'Uke 1–6',
    title: 'Oppstart og oppbygging',
    bullets: ['2-4 gå/løp-økter', '10 min styrke med strikk', 'Mindfulness'],
    text: 'Bygg gode vaner, finn rytmen og opplev mestring fra første uke.',
    image: '/img/program/bakkestranda2.jpg'
  },
  {
    week: 'Uke 7–12',
    title: 'Videreutvikling og fremgang',
    bullets: ['3-5 løpeøkter', 'Sammenhengende løping','Intervaller', '15 min styrke med strikk', 'Pust deg i form'],
    text: 'Øk utholdenheten, bli sterkere og løp 30 minutter sammenhengende.',
    image: '/img/program/bakkestranda1.jpg'
  }
];

const valueCards = [
  {
    title: 'Mindfulness',
    subtitle: '2–3 ganger i uken',
    ingress: 'I programmet jobber du med pusten gjennom korte mindfullness-økter. God pust gir deg mer ro, bedre oksygentilførsel og lavere stress - både i hverdagen og når du løper.',
    icon: 'fas fa-spa',
    bullets: ['Bedre pustekontroll', 'Bedre ro og fokus', 'Mindre stress', 'Bedre restitusjonog søvn', 'Mental sterkere i krevende økter'],
    image: '/img/program/lg-mindfullness.jpg',
    catchPhrase: "Korte økter som gjør en stor forskjell"
  },
  {
    title: 'Styrke for løping',
    subtitle: '1–3 ganger i uken',
    ingress: 'Hver uke får du en styrkeøkt med strikk som bygger deg sterkere, mer stabil og mindre skader. Fra uke 7 øker vi til 15 minutter for å gi deg enda bedre effekt.',
    icon: 'fas fa-dumbbell',
    bullets: ['Sterkere bein og sete', 'Mindre risiko for skader', 'Bedre løpsøkonomi', 'Mer energi i hverdagen'],
    image: '/img/program/lg-strikk.jpg',
    catchPhrase: "Bygget for å gjøre deg til en bedre løper"
  },
  {
    title: '3 gå/løpe-økter hver uke',
    subtitle: '',
    ingress: 'Tre gå/løpe-økter i uken som gradvis bygger deg fra intervaller og korte drag til 30 minutter sammenhengende løping.',
    icon: 'fas fa-running',
    bullets: ['Varierte og motiverende økter', 'Riktig intensitet for fremgang', 'Trygg og effektiv progresjon', 'Bygget for å passe en travel hverdag'],
    image: '/img/program/lg-stress.jpg',
    catchPhrase: "Du får en plan – vi heier på deg hele veien"
  }
];

const testimonials = [
  {
    src: '/img/testimonials/lg-testimonial1.png',
    alt: 'Anbefaling fra Anita, 45 år, fersk løper'
  },
  {
    src: '/img/testimonials/lg-testimonial2.png',
    alt: 'Anbefaling fra Astrid, 50 år'
  },
  {
    src: '/img/testimonials/lg-testimonial3.png',
    alt: 'Anbefaling fra Birgit, 39 år'
  }
];

const LOGO_SRC = '/lopeglad_white.png';
const FOOTER_LOGO_SRC = '/lopeglad_white.png';

const heroFeatures = [
  'Ubegrenset tilgang til økter',
  'Lag dine egne spillelister',
  'Finn ditt nivå',
  'Lagre dine favoritter'
];

const trustBarItems = [
  { icon: 'fas fa-layer-group', label: 'Tilpasset ditt nivå' },
  { icon: 'fas fa-users', label: 'Norsk stemme' },
  { icon: 'fas fa-stopwatch', label: 'Tren når det passer deg' },
  { icon: 'fas fa-tag', label: 'Trygt og effektivt' },
  { icon: 'fas fa-shield-alt', label: 'Bygget for varige resultater' }
];

/** 149 NOK in øre/cents for Fathom _value */
const TRIAL_CTA_VALUE_CENTS = 14900;

const trackFathomEvent = (eventName, options) => {
  if (typeof window === 'undefined' || typeof window.fathom?.trackEvent !== 'function') {
    return;
  }
  if (options) {
    window.fathom.trackEvent(eventName, options);
  } else {
    window.fathom.trackEvent(eventName);
  }
};

const Lop30MinutterLanding = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=6741588236, app-argument=${getLop30ProgramDeepLink()}`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const trackStartTrial = () => {
    trackFathomEvent('Start gratis i 7 dager', { _value: TRIAL_CTA_VALUE_CENTS });
  };

  const createAccountUrl = appendUtmToUrl(CREATE_ACCOUNT_LOP30_URL, getStoredUtmParams());

  const handleOpenInApp = (event) => {
    event.preventDefault();
    trackFathomEvent('Åpne programmet i appen');
    tryOpenAppThenStore(appendUtmToUrl(getLop30ProgramDeepLink(), getStoredUtmParams()));
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={`lop30${menuOpen ? ' lop30--menu-open' : ''}`}>
      <header className="lop30-header">
        <div className="lop30-container">
          <div className="lop30-header-inner">
            <div className="lop30-header-brand">
              <Link to="/" onClick={closeMenu}>
                <img src={LOGO_SRC} alt="Løpeglad" className="lop30-logo" />
              </Link>
            </div>
            <nav className="lop30-nav lop30-nav--desktop" aria-label="Hovedmeny">
              <a href="#appen">Appen</a>
              <a href="#om">Om Løpeglad</a>
              <a href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st">Podcast</a>
            </nav>
            <div className="lop30-header-actions">
              <div className="lop30-header-cta">
                <Link
                  to={createAccountUrl}
                  className="lop30-btn lop30-btn--header-cta"
                  onClick={trackStartTrial}
                >
                  Start gratis i 7 dager
                </Link>
              </div>
              <button
                type="button"
                className={`lop30-menu-toggle${menuOpen ? ' is-open' : ''}`}
                aria-expanded={menuOpen}
                aria-controls="lop30-mobile-menu"
                aria-label={menuOpen ? 'Lukk meny' : 'Åpne meny'}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="lop30-menu-toggle__bars" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          className={`lop30-menu-backdrop${menuOpen ? ' is-open' : ''}`}
          aria-hidden={!menuOpen}
          onClick={closeMenu}
        />

        <div
          id="lop30-mobile-menu"
          className={`lop30-mobile-menu${menuOpen ? ' is-open' : ''}`}
          aria-hidden={!menuOpen}
          {...(!menuOpen ? { inert: '' } : {})}
        >
          <nav className="lop30-mobile-menu__nav" aria-label="Mobilmeny">
            <a href="#appen" onClick={closeMenu}>Appen</a>
            <a href="#om" onClick={closeMenu}>Om Løpeglad</a>
            <a
              href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st"
              onClick={closeMenu}
            >
              Podcast
            </a>
            <Link
              to={`/programs/${LOP30_PROGRAM_ID}`}
              className="lop30-mobile-menu__program"
              onClick={closeMenu}
            >
              Se hele programmet
            </Link>
          </nav>
          <Link
            to={createAccountUrl}
            className="lop30-btn lop30-btn--cta lop30-btn--cta-lg lop30-mobile-menu__cta"
            onClick={() => {
              trackStartTrial();
              closeMenu();
            }}
          >
            Start gratis i 7 dager
          </Link>
        </div>
      </header>

      <section className="lop30-hero">
        <div className="lop30-hero-bg" aria-hidden="true">
          <img src="/img/background image.jpg" alt="" />
        </div>
        <div className="lop30-hero-overlay" aria-hidden="true" />
        <div className="lop30-container lop30-hero-inner">
          <div className="lop30-hero-copy">
            <span className="lop30-badge">12 uker – steg for steg</span>
            <h1>Lær å løpe 30 minutter sammenhengende</h1>
            <p className="lop30-hero-tagline">
              – uten stress, press og følelsen av å «mislykkes»
            </p>
            <p className="lop30-hero-desc">
              Et steg-for-steg-program som tar deg fra gå/løp til 30 minutter sammenhengende løping.
              Bygget på glede, progresjon og mestring.
            </p>
            <div className="lop30-hero-actions">
              <Link
                to={createAccountUrl}
                className="lop30-btn lop30-btn--cta lop30-btn--cta-lg"
                onClick={trackStartTrial}
              >
                Start gratis i 7 dager
              </Link>
    
              <button
                type="button"
                className="lop30-btn lop30-btn--outline"
                onClick={handleOpenInApp}
              >
                <i className="fas fa-mobile-alt" aria-hidden="true" />
                Åpne programmet i appen
              </button>
            </div>
            <div className="lop30-hero-trust-row">
              <div className="lop30-trust">
                <span><i className="far fa-calendar-check" aria-hidden="true" />Ingen bindingstid</span>
                <span><i className="fas fa-music" aria-hidden="true" />Tilpasset musikk på frekvens</span>
                <span><i className="fas fa-headphones" aria-hidden="true" />Norsk stemme</span>
              </div>
              <div className="lop30-hero-price-badge lop30-hero-price-badge--mobile">
                <p className="lop30-hero-price-label">Full tilgang</p>
                <p className="lop30-hero-price-amount">149 kr</p>
                <p className="lop30-hero-price-period">/ mnd</p>
              </div>
            </div>
          </div>
          <aside className="lop30-hero-aside" aria-label="Pris og fordeler">
            <div className="lop30-hero-price-badge lop30-hero-price-badge--desktop">
              <p className="lop30-hero-price-label">Full tilgang</p>
              <p className="lop30-hero-price-amount">149 kr</p>
              <p className="lop30-hero-price-period">/ mnd</p>
            </div>
            <div className="lop30-hero-features-box">
              <ul className="lop30-hero-features-list">
                {heroFeatures.map((feature) => (
                  <li key={feature}>
                    <span className="lop30-hero-feature-icon" aria-hidden="true">
                      <i className="fas fa-check-circle" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="lop30-section">
        <div className="lop30-container">
          <div className="lop30-features">
            {highlightCards.map((card) => (
              <article key={card.title} className="lop30-feature-card">
                <span className="lop30-feature-card__icon" aria-hidden="true">
                  <i className={card.icon} />
                </span>
                <div className="lop30-feature-card__body">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lop30-section">
        <div className="lop30-container">
          <h2 className="lop30-section-title">Programmet: 12 uker til 30 minutters løping</h2>
          <p className="lop30-section-subtitle">
            Hver uke har 3-5 økter som bygger på hverandre. Du løper like økter i to uker om gangen de første 6 ukene for å skape trygghet, deretter ny progresjon hver uke.
          </p>

          <div className="lop30-program-cta">
            <Link
              to={`/programs/${LOP30_PROGRAM_ID}`}
              className="lop30-btn lop30-btn--cta lop30-btn--cta-lg"
            >
              Se hele programmet
            </Link>
          </div>
          <div className="lop30-program-grid">
            {planCards.map((card, index) => (
              <React.Fragment key={card.title}>
                {index > 0 && (
                  <div className="lop30-program-arrow" aria-hidden="true">
                    <i className="fas fa-arrow-right" />
                  </div>
                )}
                <article className="lop30-program-card">
                  <div className="lop30-program-card-media">
                    <img src={card.image} alt="" />
                    <span className="lop30-program-tag">{card.week}</span>
                  </div>
                  <div className="lop30-program-body">
                    <h3>{card.title}</h3>
                    <ul className="lop30-check-list">
                      {card.bullets.map((bullet) => (
                        <li key={bullet}>
                          <i className="far fa-check-circle" aria-hidden="true" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    <p className="lop30-program-text">
                      {card.text}
                    </p>
                  </div>
                </article>
              </React.Fragment>
            ))}
          </div>
          <ProgramGoalTimeline />
          <div className="lop30-video-block">
            <h2 className="lop30-section-title">Se video</h2>
            <div className="lop30-video-frame">
              <video
                className="lop30-video"
                controls
                playsInline
                preload="metadata"
                aria-label="Løpeglad høstkampanje 2026"
              >
                <source src="/video/lopeglad_hostkampanje_2026_4K.mp4" type="video/mp4" />
                Videoen kan ikke spilles av i denne nettleseren.
              </video>
            </div>
          </div>
        </div>
      </section>

      <section className="lop30-section" id="om">
        <div className="lop30-container">
          <h2 className="lop30-section-title">Mer enn bare et løpeprogram</h2>
          <p className="lop30-section-subtitle">
            I Løpeglad får du verktøyene du trenger for å lykkes – på dine premisser.
          </p>
          <div className="lop30-value-grid">
            {valueCards.map((card) => (
              <article key={card.title} className="lop30-value-card">
                <div className="lop30-value-card-media">
                  <img src={card.image} alt="" />
                  <span className="lop30-value-icon" aria-hidden="true">
                    <i className={card.icon} />
                  </span>
                </div>
                <div className="lop30-value-body">
                  <h3>{card.title}</h3>
                  <p className="lop30-value-sub">{card.subtitle}</p>
                  <p className="lop30-value-ingress">{card.ingress}</p>
                  <ul className="lop30-check-list">
                    {card.bullets.map((bullet) => (
                      <li key={bullet}>
                        <i className="fas fa-check" aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <p className="lop30-value-catch-phrase">{card.catchPhrase}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lop30-section">
        <div className="lop30-container">
          <h2 className="lop30-section-title">Dette sier våre medlemmer</h2>
          <div className="lop30-testimonials">
            {testimonials.map((item) => (
              <figure key={item.src} className="lop30-testimonial">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="lop30-testimonial-image"
                  loading="lazy"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="lop30-section lop30-cta-section" id="appen">
        <div className="lop30-container">
          <div className="lop30-cta-block">
            <div className="lop30-cta-copy">
              <h2>Klar for å starte reisen?</h2>
              <p>
                Start gratis i 7 dager og få tilgang til hele programmet i appen – helt uten bindingstid.
              </p>
            </div>
            <div className="lop30-cta-action">
              <Link
                to={createAccountUrl}
                className="lop30-btn lop30-btn--cta-banner"
                onClick={trackStartTrial}
              >
                Start gratis i 7 dager
                <span className="lop30-cta-arrow" aria-hidden="true">→</span>
              </Link>
    
              <button
                type="button"
                className="lop30-btn lop30-btn--outline lop30-btn--open-app"
                onClick={handleOpenInApp}
              >
                <i className="fas fa-mobile-alt" aria-hidden="true" />
                Åpne programmet i appen
              </button>
              <p className="lop30-cta-note">
                <i className="fas fa-lock" aria-hidden="true" />
                Avslutt når du vil. Ingen bindingstid.
              </p>
            </div>
            <div className="lop30-store-badges">
              <a
                href="https://apps.apple.com/no/app/l%C3%B8peglad/id6741588236?l=nb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/AppStore.png" alt="Last ned på App Store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.app.lopeglad"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/GooglePlayNorwegian.png" alt="Last ned på Google Play" />
              </a>
            </div>
          </div>
          <div className="lop30-trust-bar">
            {trustBarItems.map((item) => (
              <span key={item.label}>
                <i className={item.icon} aria-hidden="true" />
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="lop30-podcast" id="podcast">
        <div className="lop30-container lop30-podcast-inner">
          <img src="/img/Podkast-02.png" alt="Løpeglad Podkast" className="lop30-podcast-art" />
        </div>
      </section> */}

      <footer className="lop30-site-footer">
        <div className="lop30-container lop30-site-footer-inner">
          <Link to="/" className="lop30-site-footer-logo">
            <img src={FOOTER_LOGO_SRC} alt="Løpeglad" />
          </Link>
          <nav className="lop30-site-footer-nav" aria-label="Bunntekst">
            <a href="#appen">Appen</a>
            <a href="#om">Om Løpeglad</a>
            <a href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st">Podcast</a>
          </nav>
          <div className="lop30-social lop30-social--footer">
            <a href="https://www.instagram.com/lopeglad.no/" aria-label="Instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="https://www.facebook.com/lopeglad.no" aria-label="Facebook">
              <i className="fab fa-facebook" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Lop30MinutterLanding;
